import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useT, useI18n } from "@/lib/i18n";
import { verfahrenLabels } from "@/lib/translations";
import StlViewer from "@/components/StlViewer";
import OfficeStatus from "@/components/OfficeStatus";
import UpcomingHolidays from "@/components/UpcomingHolidays";
import TodayClosedBanner from "@/components/TodayClosedBanner";
import LocationMap from "@/components/LocationMap";
import {
  ArrowUpRight, Mail, Phone, MapPin, CheckCircle2, Clock,
  Paperclip, Sparkles, X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "", email: "", company: "", phone: "",
  service_interest: "", message: "", consent: false,
};

const VERFAHREN = [
  "Verzinken", "Trommelverzinken", "Zink-Nickel", "Zink-Mangan-Phosphatieren",
  "CNS-Beizen", "CNS-Elektropolieren", "Schleifen · Bürsten",
  "Brünieren", "Vergolden", "Chemisch-Nickel", "Galvanisch-Nickel",
];

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png", ".dwg", ".step", ".stp", ".stl"];
const MAX_BYTES = 10 * 1024 * 1024;

const COPY = {
  de: {
    intro: "Schicken Sie uns Ihre Bauteildaten, das gewünschte Verfahren und Ihren Zeitplan — gerne mit Bauteilzeichnung. Wir melden uns innerhalb eines Werktags persönlich zurück.",
    location: "Standort",
    phone_label: "Telefon",
    email_label: "E-Mail",
    hours_label: "Öffnungszeiten",
    hours_body: ["Mo–Do 07:00–12:00 · 13:00–17:00", "Fr 07:00–12:00 · 13:00–16:00"],
    name: "Name", email: "E-Mail", company: "Firma", phone: "Telefon",
    name_ph: "Max Muster", email_ph: "max@firma.ch", company_ph: "Muster AG", phone_ph: "+41 ...",
    service: "Verfahren",
    service_ph: "Verfahren auswählen…",
    other: "Anderes",
    message: "Nachricht *",
    message_ph: "Werkstoff, Geometrie, Stückzahl, gewünschtes Verfahren, Termin…",
    drawing: "Bauteilzeichnung (optional)",
    drawing_hint: "PDF · DWG · STEP · JPG · PNG · max. 10 MB",
    drawing_choose: "Datei wählen →",
    consent: "Ja, ich akzeptiere die Datenschutzerklärung. Meine Anfrage wird ausschließlich zur Bearbeitung durch die Fritz Kamm AG genutzt.",
    note_below_form: "Eingaben gehen direkt an unser Sekretariat in Mels. Wir geben keine Daten an Dritte weiter.",
    submit: "Anfrage senden",
    sending: "Wird gesendet…",
    success_title: "Anfrage erhalten.",
    success_body: "Vielen Dank. Ein Mitarbeiter der Fritz Kamm AG meldet sich innerhalb eines Werktags persönlich bei Ihnen. Bei dringenden Anliegen erreichen Sie uns telefonisch unter +41 81 723 16 42.",
    success_again: "Weitere Anfrage senden",
    prefill_toast: "Antworten aus dem KI-Berater wurden übernommen — bitte ergänzen Sie Ihre Kontaktdaten.",
    file_remove_aria: "Datei entfernen",
    err_required: "Bitte Name, E-Mail und Nachricht ausfüllen.",
    err_consent: "Bitte Datenschutzerklärung bestätigen.",
    err_format: (list) => `Format nicht erlaubt. Erlaubt: ${list}`,
    err_size: "Datei zu groß (max. 10 MB).",
    err_send_default: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
    err_send: "Übermittlung fehlgeschlagen.",
    ok_sent: "Anfrage erhalten. Wir melden uns innerhalb eines Werktags.",
    prefill_msg_prefix: "Aus dem KI-Berater übernommen:",
  },
  en: {
    intro: "Send us your part data, the desired process and your timeline — ideally with a component drawing. We'll respond personally within one business day.",
    location: "Location",
    phone_label: "Phone",
    email_label: "Email",
    hours_label: "Opening hours",
    hours_body: ["Mon–Thu 7am–12pm · 1pm–5pm", "Fri 7am–12pm · 1pm–4pm"],
    name: "Name", email: "Email", company: "Company", phone: "Phone",
    name_ph: "Jane Doe", email_ph: "jane@company.com", company_ph: "Acme Ltd.", phone_ph: "+41 ...",
    service: "Process",
    service_ph: "Choose a process…",
    other: "Other",
    message: "Message *",
    message_ph: "Material, geometry, quantity, desired process, deadline…",
    drawing: "Component drawing (optional)",
    drawing_hint: "PDF · DWG · STEP · STL · JPG · PNG · max. 10 MB",
    drawing_choose: "Choose file →",
    consent: "I accept the privacy policy. My inquiry will be used exclusively by Fritz Kamm AG for processing.",
    note_below_form: "Submissions go directly to our office in Mels. We never share your data with third parties.",
    submit: "Send request",
    sending: "Sending…",
    success_title: "Request received.",
    success_body: "Thank you. A Fritz Kamm AG team member will respond personally within one business day. For urgent matters please call +41 81 723 16 42.",
    success_again: "Send another request",
    prefill_toast: "Answers from the AI advisor were applied — please complete your contact details.",
    file_remove_aria: "Remove file",
    err_required: "Please complete name, email and message.",
    err_consent: "Please confirm the privacy policy.",
    err_format: (list) => `Format not allowed. Allowed: ${list}`,
    err_size: "File too large (max. 10 MB).",
    err_send_default: "Something went wrong. Please try again.",
    err_send: "Submission failed.",
    ok_sent: "Request received. We'll respond within one business day.",
    prefill_msg_prefix: "Imported from the AI advisor:",
  },
  fr: {
    intro: "Envoyez-nous les données de votre pièce, le procédé souhaité et votre planning — idéalement avec un plan. Nous vous répondons personnellement dans le délai d'un jour ouvré.",
    location: "Site",
    phone_label: "Téléphone",
    email_label: "E-mail",
    hours_label: "Horaires d'ouverture",
    hours_body: ["Lun–Jeu 7h–12h · 13h–17h", "Ven 7h–12h · 13h–16h"],
    name: "Nom", email: "E-mail", company: "Société", phone: "Téléphone",
    name_ph: "Jean Dupont", email_ph: "jean@societe.ch", company_ph: "Société SA", phone_ph: "+41 ...",
    service: "Procédé",
    service_ph: "Choisir un procédé…",
    other: "Autre",
    message: "Message *",
    message_ph: "Matériau, géométrie, quantité, procédé souhaité, délai…",
    drawing: "Plan de pièce (facultatif)",
    drawing_hint: "PDF · DWG · STEP · STL · JPG · PNG · max. 10 Mo",
    drawing_choose: "Choisir un fichier →",
    consent: "J'accepte la politique de confidentialité. Ma demande sera utilisée exclusivement par Fritz Kamm SA pour son traitement.",
    note_below_form: "Vos données sont directement transmises à notre bureau de Mels. Aucune donnée n'est transmise à des tiers.",
    submit: "Envoyer la demande",
    sending: "Envoi en cours…",
    success_title: "Demande reçue.",
    success_body: "Merci. Un collaborateur de Fritz Kamm SA vous contactera personnellement dans le délai d'un jour ouvré. Pour les demandes urgentes, appelez le +41 81 723 16 42.",
    success_again: "Envoyer une autre demande",
    prefill_toast: "Les réponses du conseiller IA ont été reprises — veuillez compléter vos coordonnées.",
    file_remove_aria: "Supprimer le fichier",
    err_required: "Veuillez remplir nom, e-mail et message.",
    err_consent: "Veuillez accepter la politique de confidentialité.",
    err_format: (list) => `Format non autorisé. Autorisés : ${list}`,
    err_size: "Fichier trop volumineux (max. 10 Mo).",
    err_send_default: "Une erreur s'est produite. Veuillez réessayer.",
    err_send: "L'envoi a échoué.",
    ok_sent: "Demande reçue. Nous vous répondons sous un jour ouvré.",
    prefill_msg_prefix: "Repris du conseiller IA :",
  },
  it: {
    intro: "Inviateci i dati del componente, il procedimento desiderato e i tempi — idealmente con un disegno. Vi risponderemo personalmente entro un giorno lavorativo.",
    location: "Sede",
    phone_label: "Telefono",
    email_label: "E-mail",
    hours_label: "Orari di apertura",
    hours_body: ["Lun–Gio 7:00–12:00 · 13:00–17:00", "Ven 7:00–12:00 · 13:00–16:00"],
    name: "Nome", email: "E-mail", company: "Azienda", phone: "Telefono",
    name_ph: "Mario Rossi", email_ph: "mario@azienda.ch", company_ph: "Azienda SA", phone_ph: "+41 ...",
    service: "Procedimento",
    service_ph: "Scelga un procedimento…",
    other: "Altro",
    message: "Messaggio *",
    message_ph: "Materiale, geometria, quantità, procedimento desiderato, scadenza…",
    drawing: "Disegno del componente (facoltativo)",
    drawing_hint: "PDF · DWG · STEP · STL · JPG · PNG · max. 10 MB",
    drawing_choose: "Scegli file →",
    consent: "Accetto l'informativa sulla privacy. La mia richiesta sarà utilizzata esclusivamente da Fritz Kamm SA per la gestione.",
    note_below_form: "I Suoi dati arrivano direttamente alla nostra segreteria a Mels. Non condividiamo dati con terzi.",
    submit: "Invia richiesta",
    sending: "Invio in corso…",
    success_title: "Richiesta ricevuta.",
    success_body: "Grazie. Un collaboratore di Fritz Kamm SA La contatterà personalmente entro un giorno lavorativo. Per richieste urgenti chiami il +41 81 723 16 42.",
    success_again: "Invia un'altra richiesta",
    prefill_toast: "Le risposte del consulente IA sono state riprese — completi i Suoi dati di contatto.",
    file_remove_aria: "Rimuovi file",
    err_required: "Compili nome, e-mail e messaggio.",
    err_consent: "Accetti l'informativa sulla privacy.",
    err_format: (list) => `Formato non consentito. Ammessi: ${list}`,
    err_size: "File troppo grande (max. 10 MB).",
    err_send_default: "Qualcosa è andato storto. Riprovi.",
    err_send: "Invio non riuscito.",
    ok_sent: "Richiesta ricevuta. Risponderemo entro un giorno lavorativo.",
    prefill_msg_prefix: "Ripreso dal consulente IA:",
  },
};

export const Contact = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const labelMap = verfahrenLabels[lang] || verfahrenLabels.de;
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [prefilledFromAssistant, setPrefilledFromAssistant] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const onPrefill = (e) => {
      const d = e.detail || {};
      setForm((f) => ({
        ...f,
        service_interest: d.service_interest || f.service_interest,
        message: d.message ? `${c.prefill_msg_prefix}\n${d.message}` : f.message,
      }));
      setPrefilledFromAssistant(true);
      setTimeout(() => setPrefilledFromAssistant(false), 6000);
    };
    window.addEventListener("fk:prefill-contact", onPrefill);
    return () => window.removeEventListener("fk:prefill-contact", onPrefill);
  }, [c.prefill_msg_prefix]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) { toast.error(c.err_format(ALLOWED_EXT.join(", "))); e.target.value = ""; return; }
    if (f.size > MAX_BYTES) { toast.error(c.err_size); e.target.value = ""; return; }
    setFile(f);
  };

  const removeFile = () => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error(c.err_required); return; }
    if (!form.consent) { toast.error(c.err_consent); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name); fd.append("email", form.email); fd.append("message", form.message);
      if (form.company) fd.append("company", form.company);
      if (form.phone) fd.append("phone", form.phone);
      if (form.service_interest) fd.append("service_interest", form.service_interest);
      fd.append("source", "contact_form");
      if (file) fd.append("file", file);

      await axios.post(`${API}/consultation`, fd);
      setSuccess(true);
      setForm(initialForm);
      setFile(null);
      toast.success(c.ok_sent);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data) ? err.response.data[0]?.msg : null) ||
        c.err_send_default;
      toast.error(typeof detail === "string" ? detail : c.err_send);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="overline reveal mb-5">{t("sections.kontakt_overline", "— Kontakt")} · 07 / 07</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.kontakt_title_1", "Holen Sie eine Offerte")}
              <br />
              <span className="text-zinc-500">{t("sections.kontakt_title_2", "auf Sie zugeschnitten ein.")}</span>
            </h2>

            <p className="reveal reveal-delay-2 mt-8 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>

            <div className="reveal reveal-delay-2 mt-8 max-w-md">
              <TodayClosedBanner />
            </div>

            <div className="reveal reveal-delay-3 mt-12 space-y-6 max-w-md">
              <div className="flex items-start gap-4 pb-6 border-b border-white/10">
                <MapPin className="w-5 h-5 text-zinc-400 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="overline mb-1">{c.location}</div>
                  <div className="text-white" data-testid="contact-address">
                    Fritz Kamm AG<br />Zeughausstrasse 12<br />CH-8887 Mels
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4 pb-6 border-b border-white/10">
                <Phone className="w-5 h-5 text-zinc-400 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="overline mb-1">{c.phone_label}</div>
                  <a href="tel:+41817231642" className="text-white hover:text-zinc-300 transition-colors" data-testid="contact-phone">
                    +41 81 723 16 42
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4 pb-6 border-b border-white/10">
                <Mail className="w-5 h-5 text-zinc-400 mt-1" strokeWidth={1.5} />
                <div>
                  <div className="overline mb-1">{c.email_label}</div>
                  <div className="space-y-1 text-sm">
                    <a href="mailto:sekretariat@f-kamm.ch" className="block text-white hover:text-zinc-300 transition-colors" data-testid="contact-email">
                      sekretariat@f-kamm.ch
                    </a>
                    <a href="mailto:projekte@f-kamm.ch" className="block text-zinc-400 hover:text-white transition-colors">
                      projekte@f-kamm.ch · {lang === "en" ? "inquiries" : lang === "fr" ? "demandes" : lang === "it" ? "richieste" : "Anfragen"}
                    </a>
                    <a href="mailto:spedition@f-kamm.ch" className="block text-zinc-400 hover:text-white transition-colors">
                      spedition@f-kamm.ch · {lang === "en" ? "logistics" : lang === "fr" ? "logistique" : lang === "it" ? "logistica" : "Logistik"}
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-zinc-400 mt-1" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="overline mb-1">{c.hours_label}</div>
                  <div className="text-white text-sm leading-relaxed">
                    {c.hours_body[0]}<br />{c.hours_body[1]}
                  </div>
                  <div className="mt-3">
                    <OfficeStatus />
                  </div>
                </div>
              </div>
            </div>

            <div className="reveal reveal-delay-4 mt-8 max-w-md">
              <LocationMap />
            </div>

            <div className="reveal reveal-delay-4 mt-8 max-w-md">
              <UpcomingHolidays />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form data-testid="consultation-form" onSubmit={onSubmit} className="reveal reveal-delay-2 bg-[#121214] border border-white/10 p-8 md:p-12">
              {success ? (
                <div data-testid="consultation-success" className="flex flex-col items-start gap-6 py-10">
                  <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={1.4} />
                  <div>
                    <h3 className="font-display text-3xl text-white tracking-tight mb-2">{c.success_title}</h3>
                    <p className="text-zinc-400 max-w-md leading-relaxed">{c.success_body}</p>
                  </div>
                  <button type="button" onClick={() => setSuccess(false)} data-testid="consultation-success-reset" className="mt-4 inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-sm font-semibold hover:bg-white/5 transition-colors">
                    {c.success_again}
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <>
                  {prefilledFromAssistant && (
                    <div data-testid="contact-prefill-toast" className="mb-6 flex items-center gap-2.5 px-3 py-2 bg-white/5 border border-white/15 text-[12px] text-zinc-200">
                      <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {c.prefill_toast}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <label className="overline block mb-2">{c.name} *</label>
                      <input data-testid="form-name" className="field" name="name" value={form.name} onChange={onChange} placeholder={c.name_ph} required />
                    </div>
                    <div>
                      <label className="overline block mb-2">{c.email} *</label>
                      <input data-testid="form-email" type="email" className="field" name="email" value={form.email} onChange={onChange} placeholder={c.email_ph} required />
                    </div>
                    <div>
                      <label className="overline block mb-2">{c.company}</label>
                      <input data-testid="form-company" className="field" name="company" value={form.company} onChange={onChange} placeholder={c.company_ph} />
                    </div>
                    <div>
                      <label className="overline block mb-2">{c.phone}</label>
                      <input data-testid="form-phone" className="field" name="phone" value={form.phone} onChange={onChange} placeholder={c.phone_ph} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="overline block mb-2">{c.service}</label>
                      <select data-testid="form-service" name="service_interest" value={form.service_interest} onChange={onChange} className="field appearance-none cursor-pointer">
                        <option value="" className="bg-[#121214]">{c.service_ph}</option>
                        {VERFAHREN.map((v) => (
                          <option key={v} value={v} className="bg-[#121214]">{labelMap[v] || v}</option>
                        ))}
                        <option value="Anderes" className="bg-[#121214]">{c.other}</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-8">
                    <label className="overline block mb-2">{c.message}</label>
                    <textarea data-testid="form-message" name="message" value={form.message} onChange={onChange} className="field" placeholder={c.message_ph} rows={4} required />
                  </div>

                  <div className="mb-8">
                    <div className="overline mb-2">{c.drawing}</div>
                    <input ref={fileInputRef} type="file" onChange={onFileChange} accept={ALLOWED_EXT.join(",")} className="hidden" id="contact-file" data-testid="form-file" />
                    {!file ? (
                      <label htmlFor="contact-file" className="group flex items-center justify-between gap-3 border border-dashed border-white/20 hover:border-white/50 hover:bg-white/[0.03] px-5 py-4 cursor-pointer transition-all">
                        <span className="flex items-center gap-3 text-sm text-zinc-300">
                          <Paperclip className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" strokeWidth={1.5} />
                          {c.drawing_hint}
                        </span>
                        <span className="text-[12px] text-zinc-500 group-hover:text-white transition-colors">
                          {c.drawing_choose}
                        </span>
                      </label>
                    ) : (
                      <div data-testid="form-file-pill" className="flex items-center justify-between gap-3 bg-white/[0.04] border border-white/15 px-5 py-4">
                        <span className="flex items-center gap-3 text-sm text-white truncate">
                          <Paperclip className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                          <span className="truncate">{file.name}</span>
                          <span className="text-[12px] text-zinc-500 flex-none">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                        </span>
                        <button type="button" onClick={removeFile} data-testid="form-file-remove" className="text-zinc-400 hover:text-white p-1" aria-label={c.file_remove_aria}>
                          <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    )}
                    {file && file.name.toLowerCase().endsWith(".stl") && (
                      <StlViewer file={file} />
                    )}
                  </div>

                  <label className="flex items-start gap-3 mb-8 cursor-pointer">
                    <input type="checkbox" name="consent" data-testid="form-consent" checked={form.consent} onChange={onChange} className="mt-1 w-4 h-4 accent-white" />
                    <span className="text-[12px] text-zinc-400 leading-relaxed">{c.consent}</span>
                  </label>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p className="text-[12px] text-zinc-500 max-w-sm">{c.note_below_form}</p>
                    <button type="submit" data-testid="form-submit" disabled={submitting} className="group inline-flex items-center justify-center gap-2 bg-white text-black px-7 py-4 text-sm font-semibold tracking-tight hover:bg-zinc-200 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed">
                      {submitting ? c.sending : c.submit}
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
